import { IContentBlockData, TextContentBlockType } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { TextAtom } from "src/lib/cheshire-cat/models/text-atom.model";

export interface ContentBlockData {
    contentBlock: IContentBlockData;
    textMapItems: TextAtom[];
}

export function processContentBlocks(contentBlock: IContentBlockData, textMap: TextAtom[], lastTextMapItemCheckedRef: { value: number }): ContentBlockData[] {
    // if there are no words to link to the content blocks, there is no point 
    if (textMap.length === 0)
        return [];

    // results can be all in 1 array because the id of each IContentBlocks seems to be unqiue within a spine element
    const accumulatedResults: ContentBlockData[] = [];
    processContentBlock(contentBlock, textMap, lastTextMapItemCheckedRef, accumulatedResults);
    return accumulatedResults;
}

function processContentBlock(contentBlock: IContentBlockData, textMap: TextAtom[], lastTextMapItemCheckedRef: { value: number }, accumulatedResults: ContentBlockData[]): void {
    let textMapItems: TextAtom[] = [];
    const textContent = contentBlock.textContent.replace(/\s+/g, ' ').trim(); // remove tabs and new lines

    // if there is text content
    if (textContent !== "" && isTextContentBlockType(contentBlock.blockType)) {
        const allowedMargin = Math.max(1, Math.floor(textContent.length * 0.05)); // allow a character mistake of 5% (randomly chosen) characters, at least 1 mistake allowed
        // I have seen examples where paragraphText would be over 10 000 words,
        // when the sentence itself is so much less then that, so to avoid checking the threshold for so many words
        // only do it if the words, more or less match
        const numberOfWords = textContent.split(' ').length;
        
        // get the next textAtom to check
        // also check if that atom and the next are in the textContent (if there is a next)
        // and also if there are more then one words in the textContent
        // if the textContent is just a punctuation, start looking from lastTextMapItemCheckedRef - 1
        const lastValue = /^[.,!?:;]+$/.test(textContent) ? lastTextMapItemCheckedRef.value - 1 : lastTextMapItemCheckedRef.value
        // the whole string is exactly one letter or digit
        // OR the string contains any non-word, non-space character anywhere
        const isSymbol = (word: string) => /^[a-zA-Z0-9]$|[^\w\s]/.test(word);
        const isNumber = (w: string) => /^\d+$/.test(w);
        const wordRegex = (word: string) => new RegExp(`(?:^|\\s|[^\\w\\d])${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$|[^\\w\\d])`, 'i');
        // Number match with digit boundaries.
        // Allows 12x/12xy, but blocks 312 and 123 matching "12".
        const numberRegex = (number: string) => new RegExp(`(?:^|\\D)${number.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?!\\d)`);

        const textAtom: TextAtom | undefined = textMap.find((x) => {
            // NOTE 04 FEB 2026
            // GMAT Foundations of Math, chapter 1 > Long addition and substraction
            // textmap is a little wrong because of a word in a span and because 'to the next column' appears in the textContent,
            // but also above it where 'lastTextMapItemCheckedRef' is
            // so when checking if the current word + next word is in the textContent, only check the first x amount of words in the textContent
            // first x amount of words => 10 => randomly chosen
            const text = normalizeText(textContent).split(' ').slice(0, numberOfWords < 10 ? numberOfWords : 10).join(' ');
            const word = normalizeText(x.atomText)
            const nextWord = textMap[x.itemId + 1] ? normalizeText(textMap[x.itemId + 1].atomText) : undefined
            const r = x.itemId >= lastValue
                    && (isSymbol(word) 
                        ? text.includes(word) 
                        : isNumber(word)
                            ? numberRegex(word).test(text)
                            : wordRegex(word).test(text)) // if current word in text
                    && (
                        nextWord && numberOfWords > 1 // if there is a next word in array and text is more then 1 word
                            ? text.replace(/\s/g, '').includes(word + (nextWord ?? ''))
                            : true // if there is no next word, ignore next word logic by returning true
                    )
            return r
        });
        if (!textAtom) {
            console.error('Could not find the next textAtom in textmap for contentBlock: ', contentBlock);
            return;
        }

        const remainingTextMap = textMap.filter((x) => x.itemId >= textAtom.itemId);
        textMapItems = findTextAtoms(textContent, remainingTextMap, numberOfWords, allowedMargin, lastTextMapItemCheckedRef);
    }

    // add items to results list
    accumulatedResults.push({
        contentBlock,
        textMapItems
    } as ContentBlockData);

    // don't forget to process the children
    contentBlock.children.forEach((x) => processContentBlock(x, textMap, lastTextMapItemCheckedRef, accumulatedResults));
}

function findTextAtoms(
    textContent: string,
    remainingTextMap: TextAtom[],
    numberOfWords: number,
    allowedMargin: number,
    lastTextMapItemCheckedRef: { value: number }
): TextAtom[] {
    const attempts = [
        () => checkAttempt1(textContent, remainingTextMap, numberOfWords, allowedMargin),
        () => checkAttempt2(textContent, remainingTextMap, numberOfWords, allowedMargin),
        () => checkAttempt3(textContent, remainingTextMap, allowedMargin), // check with spaces
        () => checkAttempt4(textContent, remainingTextMap, allowedMargin) // check without, because last hail mary?
    ];

    for (const attempt of attempts) {
        const paragraphText = attempt();
        if (paragraphText.length > 0) {
            // I have come across examples where cheshire cat puts the rest of the <p> at the end of the textMap
            // no idea why, but the next textAtom should be the next in the sequence
            const nextWordIndex = findNextTextAtomtId(paragraphText.map((x) => x.itemId));
            lastTextMapItemCheckedRef.value = nextWordIndex;
            return paragraphText
        }
    }
    console.error('ProcessContentBlock - no match found. textContent: ', textContent);
    return [];
}

function checkAttempt1(textContent: string, remainingTextMap: TextAtom[], numberOfWords: number, allowedMargin: number): TextAtom[] {
    // get all the other items in the same block element
    // areXPathsSimilarBeforeLastP() is needed because spans ...
    const paragraphText = remainingTextMap.filter((x) => areXPathsSimilarBeforeLastBlockElement(x.xpath, remainingTextMap[0].xpath));
    if (paragraphText.length === 0) {
        console.error('Could not find words in textmap for contentBlock: ', textContent);
    }

    // check if the text more or less matches, but only if numberOfWords is more or less the same as the paragraphText
    // because somethimes, paragraphText is everything and that is just way to much to check
    // 2 is to add a margin and is randomly chosen
    return (numberOfWords * 2 >= paragraphText.length
        ? isMatchAboveThreshold(textContent, join(paragraphText), allowedMargin)
        : false
    )
        ? paragraphText
        : [];
}

function checkAttempt2(textContent: string, remainingTextMap: TextAtom[], numberOfWords: number, allowedMargin: number): TextAtom[] {
    // in colibrio a <p> can get split up if there is a <br/> in
    // resulting paragraphText to be more then the words in the content block
    // and somethimes when the font changes, colibrio will see at as the same content while cheshire cat will split it
    // resulting in the textContent to be more words then the paragraphText

    // first try just matching the words, then try matching the number of characters (see try nr 3)
    let paragraphText = remainingTextMap.slice(0, numberOfWords);
    return isMatchAboveThreshold(textContent, join(paragraphText), allowedMargin)
        ? paragraphText
        : [];
}

function checkAttempt3(textContent: string, remainingTextMap: TextAtom[], allowedMargin: number): TextAtom[] {
    // somethimes the , is a separate textAtom while it is not in the textContent
    // check character per character because words, don't work
    // somethimes a space to many is added or one is forgotten, and cheshire cat does find that, but the split not
    const paragraphText = getParagraphTextBasedOnCharacters(textContent, remainingTextMap)
    return isMatchAboveThreshold(textContent, join(paragraphText), allowedMargin)
        ? paragraphText
        : [];
}

function checkAttempt4(textContent: string, remainingTextMap: TextAtom[], allowedMargin: number): TextAtom[] {
    // one last hail mary?
    // remove all the spaces from both
    // use same paragraphText from attempt 3?
    const paragraphText = getParagraphTextBasedOnCharacters(textContent, remainingTextMap)
    return isMatchAboveThreshold(textContent.replace(/\s/g, ''), join(paragraphText).replace(/\s/g, ''), allowedMargin)
        ? paragraphText
        : [];
}

function getParagraphTextBasedOnCharacters(textContent: string, remainingTextMap: TextAtom[]): TextAtom[] {
    // cumulativeOffset, becase we can not use locSymbPos, because that is within a node, not a content block
    let cumulativeOffset = (textContent.match(/ /g) || []).length;
    return remainingTextMap.filter((x) => {
        // Determine the locSymbPos of the next atom, if it exists
        // No point in checking if offset is already bigger than text
        if (cumulativeOffset <= textContent.length) {
            cumulativeOffset += x.atomText.length;
            if (cumulativeOffset <= textContent.length) {  // +1 to allow for a space at the end
                return true;
            }
        }
        return false;
    });
    }

function isTextContentBlockType(value: any): boolean {
    return Object.values(TextContentBlockType).includes(value as TextContentBlockType);
}

const BLOCK_ELEMENTS = [
    'p', 'div', 'h[1-6]', 'li', 'ul', 'ol', 'dl', 'dt', 'dd',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
    'section', 'article', 'header', 'footer', 'aside', 'main', 
    'blockquote', 'pre', 'figure', 'figcaption',
    'form', 'fieldset', 'legend', 'address'
];

export function getBeforeLastBlockElement(path: string): string {
    const regex = new RegExp(`(.*\\/(${BLOCK_ELEMENTS.join('|')})(\\[\\d+\\])?)\\/.*`);
    return path.replace(regex, '$1');
}

export function removeBeforeLastBlockElement(path: string): string {
    const regex = new RegExp(`(.*\\/(${BLOCK_ELEMENTS.join('|')})(\\[\\d+\\])?)\\/`);
    return path.replace(regex, '');
}

function areXPathsSimilarBeforeLastBlockElement(xpath1: string, xpath2: string): boolean {
    return getBeforeLastBlockElement(xpath1) === getBeforeLastBlockElement(xpath2);
}

function findNextTextAtomtId(ids: number[]): number {
    if (ids.length === 0) return 0; // Handle empty case

    // Sort the IDs in ascending order
    const sortedIds = ids.sort((a, b) => a - b);
    const start = sortedIds[0]; // Get the first ID

    // Iterate and find the first missing number
    for (let i = 0; i < sortedIds.length; i++) {
        if (sortedIds[i] !== start + i) {
            return start + i; // Return the missing number
        }
    }

    // If no gaps, return the next expected number
    return sortedIds[sortedIds.length - 1] + 1;
}

// to fix problem with fractions in a cookbook epub
export function normalizeText(text: string): string {
    return text
        .replace(/½/g, "1/2")
        .replace(/¼/g, "1/4")
        .replace(/¾/g, "3/4")
        .replace(/⅓/g, "1/3")
        .replace(/⅔/g, "2/3")
        .replace(/⅕/g, "1/5")
        .replace(/⅖/g, "2/5")
        .replace(/⅗/g, "3/5")
        .replace(/⅘/g, "4/5")
        .replace(/⅙/g, "1/6")
        .replace(/⅚/g, "5/6")
        .replace(/⅛/g, "1/8")
        .replace(/⅜/g, "3/8")
        .replace(/⅝/g, "5/8")
        .replace(/⅞/g, "7/8")
        .replace(/(\d)⁄(\d)/g, "$1/$2") // Replace Unicode fraction slash (e.g., 1⁄2 -> 1/2)
        .replace(/…/g, "...") // Replace ellipsis with three dots
        .replace(/[‘’]/g, "'") // Normalize single curly quotes to straight quotes
        .replace(/[“”]/g, '"') // Normalize double curly quotes to straight quotes
        .replace(/^\uFEFF/, '') // Remove the BOM (Byte Order Mark)
        .replace(/™/g, 'TM') // Replace the trademark symbol with 'TM'
}

function join(atoms: TextAtom[]): string {
    if (atoms.find((x) => !/[a-zA-Z0-9]/.test(x.atomText)) || atoms.find((x) => /^[A-Z]$/.test(x.atomText))) {
        return customJoin(atoms.map((x) => normalizeText(x.atomText)));
    }
    return atoms.map((x) => x.atomText).join(' ');
}

// because if the ' is not part of a text atom, but one on it's own
// to many spaces will be added
// this is also in the assumption that array is not half a sentence
// and customJoin should also be used if the first capital letter of a word is a word on it's own in the textmap
// because of html like: U<small>ITGEVERIJ</small> L<small>ANNOO</small>
export function customJoin(array: string[]): string {
    return array.reduce((acc, curr, index) => {
        if (index === 0) {
            return curr; // First element, no need to check
        }

        const prev = array[index - 1];
        // Check if the current word is a standalone quote or part of a quoted phrase
        if ((curr === "'" || curr === '"')) {
            const prevQuotes = acc.split(curr).length - 1
            // if prevQuotes even, then beginning quote, if odd -> closing
            const isLast = array.length - 1 === index;
            return prevQuotes % 2 === 0 ? acc + ' ' + curr : acc + curr + (isLast ? '' : ' ')
        } else if ((prev === "'" || prev === '"')) {
            const prevQuotes = acc.split(curr).length - 1
            // if prevQuotes even, then beginning quote, if odd -> closing
            return prevQuotes % 2 === 0 ? acc + curr : acc + curr
        } else if (/^[,.!?:;)\]]+$/.test(curr)) {
            return acc + curr; // Concatenate without a space
        } else if (/^\($/.test(prev)) {
            return acc + curr;
        } else if (!/^(["'])?[AIO]$/.test(prev) // because A and I are actually words on their own in English
            && (/^[A-Z]$/.test(prev) || /^["'][A-Z]$/.test(prev))) // fix for the uitgeverij lannoo example
        { 
            return acc + curr;
        }

        return acc + ' ' + curr; // Default behavior: add a space
    }, '');
}

// use Levenshtein Distance to decide wether 2 strings are (more or less) the same
// The Levenshtein distance between two words is the minimum number of single-character edits 
// (insertions, deletions or substitutions) required to change one word into the other
function isMatchAboveThreshold(s1: string, s2: string, allowedMargin: number = 0): boolean {
    // return similarityPercentage(normalizeText(s1), normalizeText(s2)) >= threshold;
    const normalizedS1 = normalizeText(s1);
    const normalizedS2 = normalizeText(s2);
    const distance = levenshteinDistance(normalizedS1, normalizedS2);
    // if they are similar enough, return true
    return distance <= allowedMargin
}

function levenshteinDistance(s1: string, s2: string): number {
    const len1 = s1.length;
    const len2 = s2.length;
    const dp: number[][] = [];

    for (let i = 0; i <= len1; i++) {
        dp[i] = [];
        for (let j = 0; j <= len2; j++) {
            if (i === 0) {
                dp[i][j] = j;
            } else if (j === 0) {
                dp[i][j] = i;
            } else {
                dp[i][j] = Math.min(
                    dp[i - 1][j] + 1, // deletion
                    dp[i][j - 1] + 1, // insertion
                    dp[i - 1][j - 1] + (s1[i - 1] === s2[j - 1] ? 0 : 1) // substitution
                );
            }
        }
    }
    return dp[len1][len2];
}